import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { withTranslation, useTranslation } from "react-i18next";
import { usePreCheckInContext } from "./contexts/PreCheckInContext";
import { get, isEmpty } from "lodash";
import FloatInput from "./FloatInput";
import validator from "validator";
import ReservationService from "../../backend/ReservationService";
import AddCircle from "@material-ui/icons/AddCircle";
import RemoveCircle from "@material-ui/icons/RemoveCircle";
import { ClipLoaderIcon } from "../../components/LoadingIcon";

const AddAdultWithChild = () => {
  const initialFormState = {
    adultFirstName: "",
    adultLastName: "",
    adultEmail: "",
    children: [
      {
        childFirstName: "",
        childLastName: "",
      },
    ],
  };
  const {
    propertyUid,
    bookingNumber,
    language,
    lastName,
    userEmail,
    resetBooking,
    func: {
      useFormInput,
      updateGuests,
      updateErrorPage,
      setAccessTokenData,
      updateErrorModal,
    },
    data: { presets, accessToken, booking, guests },
    components: { PreCheckInHeader, PreCheckInContainer },
  } = usePreCheckInContext();

  const history = useHistory();
  const { t } = useTranslation();
  const [formData, handleFormChange] = useFormInput(initialFormState);
  const [validateForm, setValidateForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const goBack = useCallback(() => {
    history.push(
      `/pre-checkin/${propertyUid}/guest-list-management/${bookingNumber}/${encodeURIComponent(
        lastName
      )}?language=${language}&token=${accessToken}&email=${encodeURIComponent(
        userEmail
      )}`
    );
  }, [
    propertyUid,
    bookingNumber,
    lastName,
    language,
    accessToken,
    history,
    userEmail,
  ]);

  useEffect(() => {
    if (loading) {
      if (submitted && resetBooking) {
        setSubmitted(false);
      } else if (!submitted && !resetBooking && !isEmpty(booking)) {
        setLoading(false);
        goBack();
      }
    }
  }, [loading, resetBooking, booking, submitted, goBack]);

  const addBtn = () => {
    setSubmitted(true);
    const validate =
      !isEmpty(formData.adultFirstName) &&
      !isEmpty(formData.adultLastName) &&
      !isEmpty(formData.adultEmail) &&
      validator.isEmail(formData.adultEmail) &&
      !isEmailDuplicate(formData.adultEmail);
    if (validate) {
      if (presets.demoMode) {
        keepGuestDataAndMove(prepareData());
      } else {
        requestCreateAdditionalGuest(prepareData());
      }
    }
    setValidateForm(validate);
  };

  const generateDefaultValues = () => {
    const guestId = Math.floor(Math.random() * (9999 - 1000 + 1)) + 1000;
    return {
      guestId,
      id: "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }),
      guest_id: guestId,
      avatar: null,
      account_exist: false,
      license_plate: null,
      pre_checked_in_completed: false,
    };
  };

  const keepGuestDataAndMove = (newChildrenData) => {
    let guestData = {
      ...newChildrenData.guest_details,
      firstName: newChildrenData.guest_details.first_name,
      lastName: newChildrenData.guest_details.last_name,
      ...generateDefaultValues(),
    };
    guestData.children = guestData.children.map((child) => ({
      ...child,
      ...generateDefaultValues(),
    }));
    updateAdultWithChild(guestData);
    goBack();
  };

  const updateAdultWithChild = (response) => {
    if (isEmpty(response)) return;

    guests.newGuest(response);
    updateGuests(guests);
  };

  const requestCreateAdditionalGuest = async (newChildrenData) => {
    setLoading(true);
    window.scrollTo(0, 0);
    await ReservationService.createAdditionalGuest(
      accessToken,
      propertyUid,
      newChildrenData
    )
      .then((response) => {
        updateAdultWithChild(get(response.data, "guest_details"));
        goBack();
      })
      .catch((error) => {
        if (get(error.response, "status") === 401) {
          setAccessTokenData();
        }
        console.error(error);
        updateErrorPage(true);
      });
  };

  const prepareData = () => {
    const reservationId = get(booking, "resv_id");
    const data = {
      reservation_id: reservationId,
      property_uid: propertyUid,
      guest_details: {
        first_name: formData.adultFirstName,
        last_name: formData.adultLastName,
        email: formData.adultEmail,
        children: formData.children.map((child) => ({
          first_name: child.childFirstName,
          last_name: child.childLastName,
        })),
      },
      reservation: {
        property_uid: propertyUid,
        reservation_id: reservationId,
      },
    };
    return data;
  };

  const addChild = () => {
    if (
      guests.numberOfGuests() + 1 + formData.children.length <
      booking.totalGuests
    ) {
      const newChild = {
        childFirstName: "",
        childLastName: "",
      };
      handleFormChange({
        target: {
          name: "children",
          value: [...formData.children, newChild],
        },
      });
    } else {
      updateErrorModal(true, "sorry can't add more, total guest limit reached");
    }
  };

  const removeChild = (indexToRemove) => {
    const updatedChildren = formData.children.filter(
      (_, index) => index !== indexToRemove
    );
    handleFormChange({
      target: {
        name: "children",
        value: updatedChildren,
      },
    });
  };

  const isEmailDuplicate = (email) => {
    return guests.allGuests().some((guest) => guest?.email === email);
  };

  const getEmailError = (email) => {
    if (isEmpty(email)) {
      return t(
        "PLEASE_FILL_OUT_REQUIRED",
        "Please fill all the required fields"
      );
    } else if (!validator.isEmail(email)) {
      return t("INVALID_EMAIL_GUEST", "Please enter a valid email");
    } else if (isEmailDuplicate(email)) {
      return t(
        "ERROR_EMAIL_DUPLICATED_GUEST",
        "Error: The email is duplicated with another guest"
      );
    }
    return null;
  };

  return (
    <>
      <PreCheckInContainer containerName="add-my-child">
        <PreCheckInHeader
          title={t("ADD_ADULT_WITH_CHILD")}
          headerLink
        />
        {presets && (
          <>
            <section
              id="content"
              className="relative flex flex-col px-9 w-full flex-1 md:flex-0 pt-6">
              <div className="flex flex-col h-full">
                <div className="flex mb-10 w-full">
                  <div className="font-normal m-auto">
                    <p
                      className="mb-12 type-normal large px-9"
                      style={{ color: presets.titleColor }}>
                      {t("ADD_ADULT_WITH_CHILD_DESCRIPTION")}
                    </p>

                    <div className="flex flex-col px-9">
                      <div className="pt-5 mb-24 relative">
                        <FloatInput
                          id="adultFirstName"
                          name="adultFirstName"
                          label={t("FIRSTNAME")}
                          span={`*${t("REQUIRED")}`}
                          classNameLabel="mb-6 type-title small"
                          classNameSpan="text-gray-400 type-regular small pl-2"
                          classNameInput="capitalize border-b w-full px-4 rounded-none outline-none focus:outline-none type-normal large md:mb-10"
                          onChange={handleFormChange}
                          value={formData.adultFirstName}
                          labelColor={presets.backgroundColorTopBar}
                          borderColor={presets.backgroundColorTopBar}
                        />
                        {!validateForm &&
                          submitted &&
                          isEmpty(formData.adultFirstName) && (
                            <p className="text-14 text-red-500 mt-5">
                              {t(
                                "PLEASE_FILL_OUT_REQUIRED",
                                "Please fill all the required fields"
                              )}
                            </p>
                          )}
                      </div>

                      <div className="mb-24 relative">
                        <FloatInput
                          id="adultLastName"
                          name="adultLastName"
                          label={t("LAST_NAME")}
                          span={`*${t("REQUIRED")}`}
                          classNameLabel="mb-6 type-title small"
                          classNameSpan="text-gray-400 type-regular small pl-2"
                          classNameInput="capitalize border-b w-full px-4 rounded-none outline-none focus:outline-none type-normal large md:mb-10"
                          onChange={handleFormChange}
                          value={formData.adultLastName}
                          labelColor={presets.backgroundColorTopBar}
                          borderColor={presets.backgroundColorTopBar}
                        />
                        {!validateForm &&
                          submitted &&
                          isEmpty(formData.adultLastName) && (
                            <p className="text-14 text-red-500 mt-5">
                              {t(
                                "PLEASE_FILL_OUT_REQUIRED",
                                "Please fill all the required fields"
                              )}
                            </p>
                          )}
                      </div>

                      <div className="mb-24 relative">
                        <FloatInput
                          id="adultEmail"
                          name="adultEmail"
                          label={t("EMAIL")}
                          span={`*${t("REQUIRED")}`}
                          classNameLabel="mb-6 type-title small"
                          classNameSpan="text-gray-400 type-regular small pl-2"
                          classNameInput="border-b w-full px-4 rounded-none outline-none focus:outline-none type-normal large md:mb-10"
                          onChange={handleFormChange}
                          value={formData.adultEmail}
                          labelColor={presets.backgroundColorTopBar}
                          borderColor={presets.backgroundColorTopBar}
                        />
                        {!validateForm && submitted && (
                          <>
                            <p className="text-14 text-red-500 mt-5">
                              {getEmailError(formData.adultEmail)}
                            </p>
                          </>
                        )}
                      </div>

                      <hr className="mb-6" />
                      <div className="flex flex-col">
                        {formData.children.map((child, index) => (
                          <React.Fragment key={index}>
                            <div className="flex justify-between">
                              <p
                                style={{ color: presets.backgroundColorTopBar }}
                                className="type-regular medium mb-6">
                                {index === 0 && t("CHILD")}
                              </p>
                              {index === 0 ? (
                                <div onClick={() => addChild()}>
                                  <AddCircle style={{ fontSize: 35 }} />
                                </div>
                              ) : (
                                <div onClick={() => removeChild(index)}>
                                  <RemoveCircle style={{ fontSize: 35 }} />
                                </div>
                              )}
                            </div>
                            <div className="pt-5 mb-24 relative">
                              <FloatInput
                                id={`childFirstName_${index}`}
                                name={`children[${index}].childFirstName`}
                                label={t("FIRSTNAME")}
                                span={`*${t("REQUIRED")}`}
                                classNameLabel="mb-6 type-title small"
                                classNameSpan="text-gray-400 type-regular small pl-2"
                                classNameInput="capitalize border-b w-full px-4 rounded-none outline-none focus:outline-none type-normal large md:mb-10"
                                onChange={handleFormChange}
                                value={child.childFirstName}
                                labelColor={presets.backgroundColorTopBar}
                                borderColor={presets.backgroundColorTopBar}
                              />
                            </div>

                            <div className="mb-24 relative">
                              <FloatInput
                                id={`childLastName_${index}`}
                                name={`children[${index}].childLastName`}
                                label={t("LAST_NAME")}
                                span={`*${t("REQUIRED")}`}
                                classNameLabel="mb-6 type-title small"
                                classNameSpan="text-gray-400 type-regular small pl-2"
                                classNameInput="capitalize border-b w-full px-4 rounded-none outline-none focus:outline-none type-normal large md:mb-10"
                                onChange={handleFormChange}
                                value={child.childLastName}
                                labelColor={presets.backgroundColorTopBar}
                                borderColor={presets.backgroundColorTopBar}
                              />
                            </div>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  className={`app-button-ipad type-title medium ${presets.buttonStyleName}`}
                  style={{
                    backgroundColor: presets.backgroundColorTopBar,
                    color: presets.appHomeTextCallToAction,
                  }}
                  onClick={() => addBtn()}>
                  {t("SEND_INVITE")}
                </button>
              </div>
            </section>
            <button
              className="app-button-mobile type-title medium "
              style={{
                backgroundColor: presets.backgroundColorTopBar,
                color: presets.appHomeTextCallToAction,
                position: "fixed",
                bottom: 0,
                left: 0,
              }}
              onClick={() => addBtn()}>
              {t("SEND_INVITE")}
            </button>
            {loading && (
              <div className="fixed inset-0 flex justify-center items-center bg-gray-500 opacity-50 z-50">
                <ClipLoaderIcon
                  loadingState={loading}
                  size={30}
                  color={presets.backgroundColorTopBar}
                />
              </div>
            )}
          </>
        )}
      </PreCheckInContainer>
    </>
  );
};

export default withTranslation()(AddAdultWithChild);
